import { configureApi } from '@oneview/api';
import { api } from '@oneview/api/baseApi';
import { isProduction } from '@oneview/utils/branchUtils';
import { DEFAULT_PERSIST_STATE_SCOPE, removeScopedStates } from '@orbis-systems/orbis-react-toolkit';
import { configureStore, createListenerMiddleware, isAnyOf, Middleware } from '@reduxjs/toolkit';
import { createBrowserHistory } from 'history';
import { createReduxHistoryContext } from 'redux-first-history';
import { persistStore } from 'redux-persist';
import { loginTimeout } from './auth';
import { createRootReducer, RootState } from './rootReducer';
import {
    closeAllRightTabs,
    closeAllSiblingTabsAfter,
    closeAllSiblingTabsExcept,
    closeAllTabs,
    closeAllTabsExcept,
    closeSiblingTab,
    closeTab,
} from './tabBar';
import { selectOpeningTabs } from './tabBar/tabBarSelectors';

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
    history: createBrowserHistory(),
});

export const listenerMiddleware = createListenerMiddleware<RootState>();

listenerMiddleware.startListening({
    // We need to remove persist states of all closed tabs
    matcher: isAnyOf(
        closeTab,
        closeAllRightTabs?.fulfilled,
        closeAllTabsExcept?.fulfilled,
        closeAllTabs?.fulfilled,
        closeSiblingTab?.fulfilled,
        closeAllSiblingTabsExcept?.fulfilled,
        closeAllSiblingTabsAfter?.fulfilled,
    ),
    effect: async (_, { getState, dispatch, delay }) => {
        // Delay to make sure closed tabs have been unmounted
        await delay(200);
        const state = getState();
        const tabs = selectOpeningTabs(state);
        const tabScopes = tabs.map((tab) => tab.siblingTabs[tab.activeSiblingTabIndex]!.route);
        const persistStateScopes = Object.keys(state._ortkPersistState);
        dispatch(
            removeScopedStates(
                persistStateScopes.filter(
                    (scope) => scope !== DEFAULT_PERSIST_STATE_SCOPE && !tabScopes.includes(scope),
                ),
            ),
        );
    },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const queryLoggerMiddleware = () => (next: Middleware) => (action: any) => {
    if (action.type.includes('executeQuery')) {
        console.log('Query action:', action.type);
        console.log('Query endpoint:', action.meta?.arg?.endpointName);
        console.log('Query arguments:', action.meta?.arg?.originalArgs);
        console.trace('Component trace:'); // This gives a more readable trace
    }
    return next(action);
};

const middlewares = [routerMiddleware as Middleware, listenerMiddleware.middleware, api.middleware];

if (!isProduction()) {
    // middlewares.push(queryLoggerMiddleware);
}

export const setupStore = (preloadedState?: Partial<RootState>) =>
    configureStore({
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: false,
                immutableCheck: false,
                thunk: {
                    extraArgument: configureApi({
                        onLoginTimeout: () => {
                            store.dispatch(loginTimeout());
                        },
                    }),
                },
            }).concat(...middlewares),
        reducer: createRootReducer(routerReducer),
        preloadedState,
        devTools: !isProduction()
            ? {
                  maxAge: 100,
                  predicate: (_, action) => !action.type.match(/^(@@|persist)$/),
              }
            : false,
    });

export const store = setupStore();

export const persistor = persistStore(store);

export const history = createReduxHistory(store);

export type AppStore = ReturnType<typeof setupStore>;
